import { skipToken } from '@reduxjs/toolkit/query';
import type React from 'react';
import { lazy, Suspense, useEffect } from 'react';
import { Navigate, useLocation, useParams } from 'react-router-dom';
import { PageLoading, usePageLoading } from '@/app/components/page-loading.tsx';
import { useAuth } from '@/app/hooks/auth-provider';
import { useFetchVisibleAccountsQuery } from '@/slices/account-api-slice.ts';
import { useSecurityContext } from '../../hooks/security-provider.tsx';
import { UserTypeEnum } from '../../types/user-type.ts';
import DetailsAvailability from './details/details-availability.tsx';
import EditAvailability from './details/edit-availability.tsx';
import NewAvailability from './details/new-availability.tsx';

const LazyAvailabilitySlot = lazy(() => import('./AvailabilitySlot'));

const AvailabilitySlot = (props: JSX.IntrinsicAttributes & { children?: React.ReactNode }) => {
  const { user } = useAuth();
  const { currentAccount, setCurrentAccount } = useSecurityContext();
  const params = useParams();
  const { data, isLoading } = useFetchVisibleAccountsQuery(
    params?.key && currentAccount?.key !== params.key ? {} : skipToken,
  );
  const { showPageLoading, hidePageLoading } = usePageLoading();
  const { pathname } = useLocation();

  useEffect(() => {
    if (data?.length && params?.key) {
      setCurrentAccount(data.find(x => x.key === params.key));
    }
  }, [data, params]);

  useEffect(() => {
    isLoading
      ? showPageLoading('availability-slot-lazy-account-loading')
      : hidePageLoading('availability-slot-lazy-account-loading');
  }, [isLoading]);

  return (
    <Suspense fallback={<PageLoading />}>
      {user?.userType === UserTypeEnum.DrugRep &&
      (pathname.includes('new') || pathname.includes('edit')) ? (
        <Navigate to={'/dashboard'} />
      ) : (
        <LazyAvailabilitySlot {...props} />
      )}
    </Suspense>
  );
};

export const availabilitySlotRoute = {
  path: '/availability',
  element: <AvailabilitySlot />,
  children: [
    {
      path: 'new/:key?',
      element: <NewAvailability />,
    },
    {
      path: 'edit/:key',
      element: <EditAvailability />,
    },
    {
      path: 'details/:key',
      element: <DetailsAvailability />,
    },
  ],
};

export default AvailabilitySlot;
